import React, { useEffect } from 'react'
import { motion, useAnimation } from 'framer-motion'
import { BLOB_PATH, CIRCLE_PATH } from './helpers'

export const LoopedBlob = () => {
  const controls = useAnimation()
  const rotationKeyframes = [0, -360, -360, -405, -360, -720]
  const durations = keyframesToDurations([0, 0.58, 2.04, 2.57, 3.44, 7.17])
  const repeatFromKeyframe = 5

  useEffect(() => {
    let isRunning = true
    const animateToKeyframe = async (keyframeIndex: number) => {
      if (!isRunning) {
        return
      }
      await controls.start({
        rotate: rotationKeyframes[keyframeIndex],
        transition: {
          duration: durations[keyframeIndex],
        },
      })
      // useEffect set up by useAnimation from framer-motion cleans up animations on unmount,
      // this causes the controls to immediately resolve without scheduling a microtask
      // We need to force it to schedule a microtask so that the cleanup function in this effect can run,
      // otherwise we end up with an infinite loop
      await sleep(0)
    }
    const animate = async () => {
      for (let i = 1; i < repeatFromKeyframe; i++) {
        await animateToKeyframe(i)
      }
      while (isRunning) {
        controls.set({ rotate: rotationKeyframes[repeatFromKeyframe - 1] })
        await animateToKeyframe(repeatFromKeyframe)
      }
    }
    animate()
    return () => {
      isRunning = false
    }
  }, [])

  return (
    <motion.g
      style={{ originX: 'center', originY: 'center' }}
      animate={controls}
      initial={{ rotate: rotationKeyframes[0] }}
    >
      <motion.g
        style={{ originX: 'center', originY: 'center' }}
        animate={{ scale: 0.84 }}
        transition={{ duration: 0.33 }}
      >
        <BaseBlob fill='rgb(var(--primary-color))' />
        <BaseBlob fill='white' opacityKeyframes={[0.56, 0.23, 0, 0, 0, 0, 0, 0, 0]} />
        <BaseBlob
          fill='black'
          opacityKeyframes={[0, 0, 0.24, 0.24, 0.24, 0.24, 0.24, 0.24, 0.24]}
        />
      </motion.g>
    </motion.g>
  )
}

const BaseBlob = ({ fill, opacityKeyframes }: { fill: string; opacityKeyframes?: number[] }) => {
  const controls = useAnimation()
  const pathKeyframes = [
    CIRCLE_PATH,
    BLOB_PATH,
    BLOB_PATH,
    CIRCLE_PATH,
    BLOB_PATH,
    BLOB_PATH,
    CIRCLE_PATH,
    CIRCLE_PATH,
    BLOB_PATH,
  ]
  const durations = keyframesToDurations([0, 0.33, 0.58, 1.28, 2, 3.44, 5.02, 5.75, 7.17])
  const repeatFromKeyframe = 6

  useEffect(() => {
    let isRunning = true
    const animateToKeyframe = async (keyframeIndex: number) => {
      if (!isRunning) {
        return
      }
      await controls.start({
        d: pathKeyframes[keyframeIndex],
        opacity: opacityKeyframes?.[keyframeIndex],
        transition: {
          duration: durations[keyframeIndex],
        },
      })
      // useEffect set up by useAnimation from framer-motion cleans up animations on unmount,
      // this causes the controls to immediately resolve without scheduling a microtask
      // We need to force it to schedule a microtask so that the cleanup function in this effect can run,
      // otherwise we end up with an infinite loop
      await sleep(0)
    }
    const animate = async () => {
      for (let i = 1; i < repeatFromKeyframe; i++) {
        await animateToKeyframe(i)
      }
      while (isRunning) {
        controls.set({
          opacity: opacityKeyframes?.[repeatFromKeyframe - 1],
          d: pathKeyframes[repeatFromKeyframe - 1],
        })
        for (let i = repeatFromKeyframe; i < durations.length; i++) {
          await animateToKeyframe(i)
        }
      }
    }
    animate()
    return () => {
      isRunning = false
    }
  }, [])

  return (
    <motion.path
      fill={fill}
      animate={controls}
      initial={{ opacity: opacityKeyframes?.[0], d: pathKeyframes[0] }}
    />
  )
}

const keyframesToDurations = (keyframes: number[]) => {
  let durationElapsed = 0
  return keyframes.map(keyframe => {
    const duration = keyframe - durationElapsed
    durationElapsed += duration
    return duration
  })
}

const sleep = (timeout: number) => new Promise(resolve => setTimeout(resolve, timeout))
